import * as React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import { faXmark, faAsterisk } from '@fortawesome/free-solid-svg-icons'
import * as classNames from 'classnames'

import { InputText } from '~/atoms/form/InputText'
import { InputRadio } from '~/atoms/form/InputRadio'
import { InputTextArea } from '~/atoms/form/InputTextArea'
import { InputSelect } from '~/atoms/form/InputSelect'
import { InputImage } from '~/atoms/form/InputImage'
import { InputSubmit } from '~/atoms/form/InputSubmit'
import { InformationText } from '~/atoms/text/InformationText'
import { parseDateString, calcAgeString } from '@/utils/dateFormat'
import { makeNumberArray } from '@/utils/arrayUtil'
import * as Style from './style.css'

export type IProps = {
  token: string
  children: IChildWithErrors[]
  needCredentialImage: boolean
}

type IFiled = {
  'birthday(1i)': number
  'birthday(2i)': number
  'birthday(3i)': number
}

const now = new Date()
const yearArray = makeNumberArray(now.getFullYear() - 16, now.getFullYear() + 1)
const monthArray = makeNumberArray(1, 12)
const defalutAllergyPlaceholder = `アレルギーがある場合は、アレルギー内容を詳細にご記入ください`
const acceptedAllergyPlaceholder = `＜入力例＞以下のフォーマットを参考に詳細に情報を記載しましょう。
【アレルギー食材】卵(全卵)、小麦
【アナフィラキシー経験有無】あり／なし　※ありの場合は詳細を記入
【エピペンの所持】あり／なし
`
const makeAllergyPlaceholder = (accepted?: string | boolean) =>
  accepted === 'true' || accepted === true
    ? acceptedAllergyPlaceholder
    : defalutAllergyPlaceholder
const defalutDiseasePlaceholder = `持病、障がいや発達の遅れなどの特別なケアがある場合は、内容を詳細にご記入ください`
const acceptedDiseasePlaceholder = `＜入力例＞以下のフォーマットを参考に詳細に情報を記載しましょう。
【持病／障がいの診断名】自閉症スペクトラム症
症状／特性】多動・衝動性・言語発達の遅れ(理解言語は年相応だが、会話のやりとりが難しい)
【治療・療育の状況(20XX年○月時点)】20XX年○月に診断。週に1度、2時間の療育に通っています。保育園では加配で対応をしていただいており、慣れ親しんだ環境であれば落ち着いて生活できています。
【保育中の注意点】初めての場所・人に対してはかなり敏感に反応します。事前にどこに行って何をするか、どんな人と会うか見通しを本人に伝えていただければと思います。いつも持ち歩いている電車のおもちゃがあると少し気持ちが落ち着きます。外出時は必ず持参をお願いします。
`
const makeDiseasePlaceholder = (accepted?: string | boolean) =>
  accepted === 'true' || accepted === true
    ? acceptedDiseasePlaceholder
    : defalutDiseasePlaceholder

const calcCalenderDay = (year, month) => {
  if (!year || !month) {
    return ['', ...makeNumberArray(1, 31)]
  } else if (
    2 == month &&
    (0 == year % 400 || (0 == year % 4 && 0 != year % 100))
  ) {
    return makeNumberArray(1, 29)
  }
  const lastday = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
  return makeNumberArray(1, lastday)
}

const submitHandler = (
  e: React.FormEvent<HTMLFormElement>,
  defaltChildren: IChildWithErrors[],
  fields: (IProps | IFiled)[],
) => {
  const registerCount = fields.filter(
    (field) => field['_destroy'] !== 'true',
  ).length

  // 元々子供情報が登録されていて、全て削除する場合は確認を行う
  if (defaltChildren.length > 0 && registerCount === 0) {
    if (!window.confirm('お子様情報が全て消えてしまいますがよろしいですか？')) {
      e.preventDefault()
    }
  }
}

export const ChildForm = ({
  children,
  token,
  needCredentialImage,
}: IProps): JSX.Element => {
  const { register, control, watch } = useForm({
    defaultValues: {
      user: {
        children_attributes: children.map((child) => {
          const parsedDate = parseDateString(child.birthday)
          return {
            id: child.id,
            last_name: child.last_name,
            first_name: child.first_name,
            last_name_furigana: child.last_name_furigana,
            first_name_furigana: child.first_name_furigana,
            gender: child.gender,
            birthday: child.birthday,
            'birthday(1i)': parsedDate.year,
            'birthday(2i)': parsedDate.month,
            'birthday(3i)': parsedDate.date,
            nickname: child.nickname,
            school_name: child.school_name,
            allergy_check: child.allergy_check,
            allergy: child.allergy,
            disease_check: child.disease_check,
            disease: child.disease,
            image_credential: undefined,
            _destroy: false,
            errors: child.errors,
          }
        }),
      },
    },
  })

  const { fields, append } = useFieldArray({
    control,
    name: 'user.children_attributes',
  })

  const watchAllFields = watch()

  const appendChild = () => {
    append({
      id: undefined,
      last_name: '',
      first_name: '',
      last_name_furigana: '',
      first_name_furigana: '',
      gender: 'female',
      birthday: undefined,
      'birthday(1i)': undefined,
      'birthday(2i)': undefined,
      'birthday(3i)': undefined,
      nickname: '',
      school_name: '',
      allergy_check: false,
      allergy: '',
      disease_check: false,
      disease: '',
      image_credential: undefined,
      _destroy: false,
      errors: {},
    })
  }

  return (
    <form
      method="POST"
      action="/users/update_profile_edit_child"
      encType="multipart/form-data"
      onSubmit={(e) =>
        submitHandler(e, children, watchAllFields.user.children_attributes)
      }
    >
      <input type="hidden" name="authenticity_token" value={token} />
      <div>
        {fields.map((field, index) => {
          const destroied =
            watchAllFields.user.children_attributes[index]._destroy
          const dayArray = calcCalenderDay(
            watchAllFields.user.children_attributes[index]['birthday(1i)'],
            watchAllFields.user.children_attributes[index]['birthday(2i)'],
          )
          const age = calcAgeString(
            watchAllFields.user.children_attributes[index]['birthday(1i)'],
            watchAllFields.user.children_attributes[index]['birthday(2i)'],
            watchAllFields.user.children_attributes[index]['birthday(3i)'],
          )
          const allergyPlaceholder = makeAllergyPlaceholder(
            watchAllFields.user.children_attributes[index].allergy_check,
          )
          const diseasePlaceholder = makeDiseasePlaceholder(
            watchAllFields.user.children_attributes[index].disease_check,
          )

          return (
            <div
              className={classNames({
                [Style.ChildFormField]: true,
                [Style.ChildFormFieldDeleted]: destroied,
              })}
              key={index}
            >
              <div className={Style.ChildFormFieldRowNoBorder}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    お子様のお名前
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContent}>
                  <div className={Style.ChildFormFieldItemContentName}>
                    <div className={Style.ChildFormFieldItemContentNameSei}>
                      <InputText
                        {...register(
                          `user.children_attributes.${index}.last_name`,
                        )}
                        placeholder="姓"
                        defaultValue={field.last_name}
                        name={`user[children_attributes][${index}][last_name]`}
                        isError={!!field.errors.last_name}
                      />
                    </div>
                    <div className={Style.ChildFormFieldItemContentNameMei}>
                      <InputText
                        {...register(
                          `user.children_attributes.${index}.first_name`,
                        )}
                        placeholder="名"
                        defaultValue={field.first_name}
                        name={`user[children_attributes][${index}][first_name]`}
                        isError={!!field.errors.first_name}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldItemSupplement}>
                妊娠中の場合、お子様のお名前は「未定(ミテイ)」とご記入ください
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    フリガナ
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContent}>
                  <div className={Style.ChildFormFieldItemContentName}>
                    <div className={Style.ChildFormFieldItemContentNameSei}>
                      <InputText
                        {...register(
                          `user.children_attributes.${index}.last_name_furigana`,
                        )}
                        placeholder="セイ"
                        defaultValue={field.last_name_furigana}
                        name={`user[children_attributes][${index}][last_name_furigana]`}
                        isError={!!field.errors.last_name_furigana}
                      />
                    </div>
                    <div className={Style.ChildFormFieldItemContentNameMei}>
                      <InputText
                        {...register(
                          `user.children_attributes.${index}.first_name_furigana`,
                        )}
                        placeholder="メイ"
                        defaultValue={field.first_name_furigana}
                        name={`user[children_attributes][${index}][first_name_furigana]`}
                        isError={!!field.errors.first_name_furigana}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    お子様の性別
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContentWide}>
                  <div className="form-radio">
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.gender`,
                        )}
                        label="女の子"
                        value="female"
                        name={`user[children_attributes][${index}][gender]`}
                      />
                    </div>
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.gender`,
                        )}
                        label="男の子"
                        value="male"
                        name={`user[children_attributes][${index}][gender]`}
                      />
                    </div>

                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.gender`,
                        )}
                        label="未定"
                        value="unknown_gender"
                        name={`user[children_attributes][${index}][gender]`}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    お子様の生年月日
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContentWide}>
                  <InputSelect
                    {...register(
                      `user.children_attributes.${index}.birthday(1i)`,
                    )}
                    name={`user[children_attributes][${index}][birthday(1i)]`}
                    isError={!!field.errors.birthday}
                  >
                    <option value=""></option>
                    {yearArray.map((year) => (
                      <option value={year} key={year}>
                        {year}
                      </option>
                    ))}
                  </InputSelect>{' '}
                  /{' '}
                  <InputSelect
                    {...register(
                      `user.children_attributes.${index}.birthday(2i)`,
                    )}
                    name={`user[children_attributes][${index}][birthday(2i)]`}
                    isError={!!field.errors.birthday}
                  >
                    <option value=""></option>
                    {monthArray.map((month) => (
                      <option value={month} key={month}>
                        {month}
                      </option>
                    ))}
                  </InputSelect>{' '}
                  /{' '}
                  <InputSelect
                    {...register(
                      `user.children_attributes.${index}.birthday(3i)`,
                    )}
                    name={`user[children_attributes][${index}][birthday(3i)]`}
                    withIcon={true}
                    isError={!!field.errors.birthday}
                  >
                    {dayArray.map((day) => (
                      <option value={day} key={day}>
                        {day}
                      </option>
                    ))}
                  </InputSelect>
                  <div className={Style.ChildFormFieldItemExplain}>
                    {age && <>{age}</>}
                    {!age && <>妊娠中の場合は、出産予定日をご記入ください</>}
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    お子様の愛称
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContentShort}>
                  <div className="form-layout-input">
                    <InputText
                      {...register(
                        `user.children_attributes.${index}.nickname`,
                      )}
                      defaultValue={field.nickname}
                      name={`user[children_attributes][${index}][nickname]`}
                      isError={!!field.errors.nickname}
                    />
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    保育園/学校など
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContentShort}>
                  <div className="form-layout-input">
                    <InputText
                      {...register(
                        `user.children_attributes.${index}.school_name`,
                      )}
                      defaultValue={field.school_name}
                      name={`user[children_attributes][${index}][school_name]`}
                      isError={!!field.errors.school_name}
                    />
                  </div>
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    アレルギー
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContent}>
                  <div className="form-radio">
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.allergy_check`,
                        )}
                        label="なし"
                        value="false"
                        defaultChecked={!field.allergy_check}
                        name={`user[children_attributes][${index}][allergy_check]`}
                      />
                    </div>
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.allergy_check`,
                        )}
                        label="あり"
                        value="true"
                        defaultChecked={field.allergy_check}
                        name={`user[children_attributes][${index}][allergy_check]`}
                      />
                    </div>
                  </div>
                  <InputTextArea
                    {...register(`user.children_attributes.${index}.allergy`)}
                    placeholder={allergyPlaceholder}
                    rows={4}
                    name={`user[children_attributes][${index}][allergy]`}
                    isError={!!field.errors.allergy}
                  />
                </div>
              </div>
              <div className={Style.ChildFormFieldRow}>
                <div className={Style.ChildFormFieldItemTitle}>
                  <div className={Style.ChildFormFieldItemTitleText}>
                    持病、特別なケア
                    <FontAwesomeIcon
                      icon={faAsterisk}
                      className={Style.ChildFormFieldItemTitleRequiredIcon}
                    />
                  </div>
                </div>
                <div className={Style.ChildFormFieldItemContent}>
                  <div className="form-radio">
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.disease_check`,
                        )}
                        label="なし"
                        value="false"
                        defaultChecked={!field.disease_check}
                        name={`user[children_attributes][${index}][disease_check]`}
                      />
                    </div>
                    <div style={{ marginBottom: '2px' }}>
                      <InputRadio
                        {...register(
                          `user.children_attributes.${index}.disease_check`,
                        )}
                        label="あり"
                        value="true"
                        defaultChecked={field.disease_check}
                        name={`user[children_attributes][${index}][disease_check]`}
                      />
                    </div>
                  </div>
                  <InputTextArea
                    {...register(`user.children_attributes.${index}.disease`)}
                    placeholder={diseasePlaceholder}
                    rows={4}
                    name={`user[children_attributes][${index}][disease]`}
                    isError={!!field.errors.disease}
                  />
                </div>
              </div>
              {needCredentialImage && (
                <div className={Style.ChildFormFieldRow}>
                  <div className={Style.ChildFormFieldItemTitle}>
                    <div className={Style.ChildFormFieldItemTitleText}>
                      確認書類
                      <FontAwesomeIcon
                        icon={faAsterisk}
                        className={Style.ChildFormFieldItemTitleRequiredIcon}
                      />
                    </div>
                  </div>
                  <div className={Style.ChildFormFieldItemContent}>
                    <div className="form-file style-large">
                      <InputImage
                        {...register(
                          `user.children_attributes.${index}.image_credential`,
                        )}
                        name={`user[children_attributes][${index}][image_credential]`}
                        thumbnailUrl={
                          children[index] &&
                          children[index].image_credential_present
                            ? children[index].image_credential.url
                            : undefined
                        }
                      />
                      <div className={Style.ChildFormFieldItemExplain}>
                        画像ファイルの形式：JPG / JPEG / GIF / PNG
                        <br />
                        お子様のお名前と生年月日が確認できる有効期限内の確認書類をアップロードしてください。妊娠中の方は、出産予定日が記載されている母子健康手帳をアップロードしてください。
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <input
                {...register(`user.children_attributes.${index}.id`)}
                type="hidden"
                name={`user[children_attributes][${index}][id]`}
              />
            </div>
          )
        })}
        <a className={Style.ChildFormFieldPlusButton} onClick={appendChild}>
          <FontAwesomeIcon
            icon={faSquarePlus}
            className={Style.ChildFormFieldPlusButtonIcon}
          />
          別のお子様を追加
        </a>
        <div className={Style.ChildFormInformation}>
          <InformationText title="注意事項" color="warning">
            <div className={Style.ChildFormInformationBody}>
              <ul className={Style.ChildFormInformationList}>
                <li className={Style.ChildFormInformationListItem}>
                  正しい情報をご登録ください
                </li>
                <li className={Style.ChildFormInformationListItem}>
                  アレルギー・持病をお持ちの方・
                  <a
                    className={Style.ChildFormInformationTextLink}
                    href="/help/center_detail/special_care"
                  >
                    特別なケア
                  </a>
                  (障がいや発達障がいなど)に該当する場合は、入力が必須です
                </li>
                <li className={Style.ChildFormInformationListItem}>
                  登録者（親権者の方）のお子様のみご登録可能です。ご親戚やお孫様、ご友人のお子様などはご登録いただけません
                </li>
              </ul>
              <p className={Style.ChildFormInformationSupplement}>
                上記に反する行為を確認した場合、利用を停止させていただく場合がございます。
              </p>
            </div>
          </InformationText>
        </div>
      </div>
      <div className={Style.ChildFormSubmit}>
        <div className={Style.ChildFormSubmitButton}>
          <InputSubmit name="commit" value="保存する" />
        </div>
      </div>
    </form>
  )
}
